import React from 'react';
import cn from 'classnames';
import { normalizeForSearch } from '../../utils/string.util';

type HighlightTextProps = {
	text: string;
	query: string;
	type: 'noCity' | 'withCity';
	highlightedText?: string;
};

const ABBREVIATIONS = {
	ul: 'ulica',
	al: 'aleja',
	pl: 'plac',
	os: 'osiedle',
	str: 'strada',
	bd: 'bulevardul',
	sos: 'șoseaua',
} as const;

const fold = (s: string) =>
	s
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();

const isWordChar = (ch: string) => /[0-9A-Za-z\u00C0-\u024f]/.test(ch);

/**
 * Find the [start,end) window of targetText inside fullText in a robust way.
 * 1) direct case-insensitive search
 * 2) diacritic-insensitive search
 * If not found, returns null.
 */
function locateTargetWindow(fullText: string, targetText: string) {
	const a = fullText.toLowerCase();
	const b = targetText.toLowerCase();
	let start = a.indexOf(b);
	if (start !== -1) return { start, end: start + targetText.length };

	const af = fold(fullText);
	const bf = fold(targetText);
	start = af.indexOf(bf);
	if (start !== -1) {
		return { start, end: start + targetText.length };
	}
	return null;
}

/**
 * Parse <b>…</b> segments from backend HTML, map them to the portion of text you actually render,
 * clip to that window, and merge/clean overlaps.
 */
function parseBackendHighlightsForTarget(
	htmlText: string,
	targetText: string,
	allowedFoldedTokens: Set<string>,
): Array<{ start: number; end: number }> {
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = htmlText;

	const plainText = tempDiv.textContent || '';
	const boldNodes = Array.from(tempDiv.querySelectorAll('b'));

	const win = locateTargetWindow(plainText, targetText);
	if (!win) return [];

	const findWordBounds = (s: number, e: number) => {
		let L = s;
		let R = e;
		while (L > 0 && isWordChar(plainText[L - 1])) L--;
		while (R < plainText.length && isWordChar(plainText[R])) R++;
		return { L, R };
	};

	const rangesInFull: Array<{ start: number; end: number }> = [];
	let searchFrom = 0;

	for (const b of boldNodes) {
		const t = b.textContent ?? '';
		if (!t) continue;

		const idx = plainText.indexOf(t, searchFrom);
		if (idx === -1) continue;

		const segStart = idx;
		const segEnd = idx + t.length;

		const { L, R } = findWordBounds(segStart, segEnd);
		const segFold = fold(t);
		const wholeWordFold = fold(plainText.slice(L, R));

		const isWholeWordSegment = segStart === L && segEnd === R;
		const segmentAllowed =
			isWholeWordSegment && allowedFoldedTokens.has(segFold);

		const snappedToWordAllowed = allowedFoldedTokens.has(wholeWordFold);

		if (segmentAllowed) {
			rangesInFull.push({ start: segStart, end: segEnd });
		} else if (snappedToWordAllowed) {
			rangesInFull.push({ start: L, end: R });
		}
		searchFrom = idx + t.length;
	}

	const rel = rangesInFull
		.map(({ start, end }) => ({
			start: Math.max(0, start - win.start),
			end: Math.min(targetText.length, end - win.start),
		}))
		.filter((r) => r.end > r.start && r.start < targetText.length && r.end > 0)
		.sort((a, b) => a.start - b.start);

	const merged: Array<{ start: number; end: number }> = [];
	for (const r of rel) {
		const last = merged[merged.length - 1];
		if (!last || r.start > last.end) merged.push({ ...r });
		else last.end = Math.max(last.end, r.end);
	}
	return merged;
}

/**
 * Tokenize query safely:
 * - split by non-alphanumerics (so "43/k" => ["43", "k"])
 * - drop short tokens (len < 2) to avoid noise like “k” highlighting “Polska”
 * - expand abbreviations (ul -> ulica), keep both forms
 */
function buildQueryTerms(query: string) {
	const rawTokens = normalizeForSearch(query)
		.split(/[^0-9A-Za-z\u00C0-\u024f]+/)
		.filter(Boolean);

	const tokens = rawTokens.filter((t) => t.length >= 2);

	const expanded = tokens.flatMap((term) => {
		const abbr =
			ABBREVIATIONS[term.toLowerCase() as keyof typeof ABBREVIATIONS];
		return abbr ? [term, abbr] : [term];
	});

	const seen = new Set<string>();
	const out: string[] = [];
	for (const t of expanded) {
		const f = fold(t);
		if (!seen.has(f)) {
			seen.add(f);
			out.push(t);
		}
	}
	return out;
}

/**
 * Fallback highlighter (when no backend HTML):
 * - diacritic-insensitive search in a folded copy, with word-boundary check
 * - maps indices back to original by position (fold preserves code-point length)
 * - merges overlaps
 */
function findMatchesByQuery(text: string, query: string) {
	const terms = buildQueryTerms(query);
	if (!terms.length) return [];

	const folded = fold(text);
	const matches: Array<{ start: number; end: number }> = [];

	for (const term of terms) {
		const ft = fold(term);
		if (!ft) continue;

		let from = 0;
		while (true) {
			const idx = folded.indexOf(ft, from);
			if (idx === -1) break;

			const before = idx - 1 >= 0 ? folded[idx - 1] : '';
			const after =
				idx + ft.length < folded.length ? folded[idx + ft.length] : '';

			const okLeft = !before || !isWordChar(before);
			const okRight = !after || !isWordChar(after);

			if (okLeft && okRight) {
				matches.push({ start: idx, end: idx + ft.length });
			}

			from = idx + ft.length;
		}
	}

	if (!matches.length) return [];

	matches.sort((a, b) => a.start - b.start);
	const merged: Array<{ start: number; end: number }> = [];
	for (const m of matches) {
		const last = merged[merged.length - 1];
		if (!last || m.start > last.end) {
			merged.push({ ...m });
		} else {
			last.end = Math.max(last.end, m.end);
		}
	}
	return merged;
}

const HighlightText: React.FC<HighlightTextProps> = ({
	text,
	query,
	type,
	highlightedText,
}) => {
	if (!query) return <>{text}</>;

	const highlightClassName = cn(
		'font-bold',
		type === 'noCity' ? 'text-cornflower-blue' : 'text-[#779BFB]',
	);

	const allowedFoldedTokens = new Set(
		buildQueryTerms(query).map((t) => fold(t)),
	);

	if (highlightedText && highlightedText.includes('<b>')) {
		try {
			const parts = parseBackendHighlightsForTarget(
				highlightedText,
				text,
				allowedFoldedTokens,
			);
			if (parts.length) {
				const out: React.ReactNode[] = [];
				let last = 0;
				parts.forEach((p, i) => {
					if (p.start > last) out.push(text.slice(last, p.start));
					out.push(
						<span key={`bh-${i}`} className={highlightClassName}>
							{text.slice(p.start, p.end)}
						</span>,
					);
					last = p.end;
				});
				if (last < text.length) out.push(text.slice(last));
				return <>{out}</>;
			}
		} catch (e) {}
	}

	const matches = findMatchesByQuery(text, query);
	if (!matches.length) return <>{text}</>;

	const out: React.ReactNode[] = [];
	let last = 0;
	matches.forEach((m, i) => {
		if (m.start > last) out.push(text.slice(last, m.start));
		out.push(
			<span key={`qh-${i}`} className={highlightClassName}>
				{text.slice(m.start, m.end)}
			</span>,
		);
		last = m.end;
	});
	if (last < text.length) out.push(text.slice(last));

	return <>{out}</>;
};

export default HighlightText;
